import {
  ArrowRight,
  Building2,
  Compass,
  FileText,
  Landmark,
  Search,
  StickyNote,
  UserRound,
  type LucideIcon,
} from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { searchPlatform } from './api'
import { Highlight } from './Highlight'
import type { SearchHit, SearchResult } from './types'

const MIN_CHARS = 2
const MAX_CHARS = 100
const DEBOUNCE_MS = 200

/**
 * The text the backend searches for (`parse_query`): whitespace collapsed, trimmed,
 * at most 100 characters. It is the request, the answer's key and the highlight,
 * so a hit is marked and "Niets gevonden" names exactly what was searched.
 */
function searchText(raw: string): string {
  // Cut by code points, like Python's slicing on the server.
  return Array.from(raw.replace(/\s+/g, ' ').trim()).slice(0, MAX_CHARS).join('').trim()
}

/** Icon per group key; a group without one (a new provider) gets the search icon. */
const GROUP_ICONS: Record<string, LucideIcon> = {
  startups: Building2,
  persons: UserRound,
  organisations: Landmark,
  notes: StickyNote,
  documents: FileText,
  pages: Compass,
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function Status({ children, alert = false }: { children: string; alert?: boolean }) {
  return (
    <p
      role={alert ? 'alert' : 'status'}
      className={alert ? 'px-4 py-6 text-sm text-destructive' : 'px-4 py-6 text-sm text-muted-foreground'}
    >
      {children}
    </p>
  )
}

function HitRow({ hit, icon: Icon, query }: { hit: SearchHit; icon: LucideIcon; query: string }) {
  return (
    <>
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0 self-start text-muted-foreground" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium">
            <Highlight text={hit.title} query={query} />
          </span>
          {hit.badge ? <span className="vonk-tag shrink-0">{hit.badge}</span> : null}
        </span>
        {hit.subtitle ? (
          <span className="truncate text-xs text-muted-foreground">{hit.subtitle}</span>
        ) : null}
        {hit.snippet ? (
          <span className="line-clamp-2 text-xs text-muted-foreground">
            <Highlight text={hit.snippet} query={query} />
          </span>
        ) : null}
      </span>
      {hit.date ? (
        <span className="shrink-0 self-start text-xs text-muted-foreground">
          {formatDay(hit.date)}
        </span>
      ) : null}
    </>
  )
}

/** The latest answer and the text it answers: the result, or 'failed'. */
interface Answer {
  text: string
  result: SearchResult | 'failed'
}

/**
 * The ⌘K palette (spec-62): one request per pause in typing, grouped results from
 * GET /api/v1/search, cmdk for the keyboard. An answer for older input is never
 * shown: it is kept together with the text it answers and only used while that is
 * still the text in the field.
 */
export function CommandSearch({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [answer, setAnswer] = useState<Answer | null>(null)
  // Raised to search the same text again after a failure.
  const [retries, setRetries] = useState(0)
  const text = searchText(query)

  useEffect(() => {
    if (text.length < MIN_CHARS) return
    let cancelled = false
    const timer = window.setTimeout(() => {
      searchPlatform(text).then(
        (result) => {
          if (!cancelled) setAnswer({ text, result })
        },
        () => {
          if (!cancelled) setAnswer({ text, result: 'failed' })
        },
      )
    }, DEBOUNCE_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [text, retries])

  function edit(value: string) {
    setQuery(value)
    // An error belongs to the attempt that hit it. Any edit after one starts a new
    // attempt, also an edit that leaves the search text as it was (a space), so
    // "Probeer het zo opnieuw" works by typing.
    if (answer?.result === 'failed') {
      setAnswer(null)
      setRetries((count) => count + 1)
    }
  }

  function go(href: string) {
    onClose()
    navigate(href)
  }

  const result = answer !== null && answer.text === text ? answer.result : null

  let body: ReactNode
  if (text.length < MIN_CHARS) {
    body = <Status>Typ minstens 2 tekens.</Status>
  } else if (result === null) {
    body = <Status>Zoeken…</Status>
  } else if (result === 'failed') {
    body = <Status alert>Zoeken lukt nu niet. Probeer het zo opnieuw.</Status>
  } else if (result.groups.length === 0) {
    body = <Status>{`Niets gevonden voor ‘${text}’.`}</Status>
  } else {
    body = result.groups.map((group) => {
      const icon = GROUP_ICONS[group.key] ?? Search
      const more = group.more_href
      return (
        <CommandGroup key={group.key} heading={group.label}>
          {group.hits.map((hit) => (
            <CommandItem
              key={hit.id}
              value={`${group.key}:${hit.id}`}
              onSelect={() => go(hit.href)}
              className="items-start py-2"
            >
              <HitRow hit={hit} icon={icon} query={text} />
            </CommandItem>
          ))}
          {more ? (
            <CommandItem
              value={`${group.key}:more`}
              onSelect={() => go(more)}
              className="text-muted-foreground"
            >
              <ArrowRight aria-hidden className="size-4 shrink-0" />
              {`Toon alle ${group.label.toLowerCase()}`}
            </CommandItem>
          ) : null}
        </CommandGroup>
      )
    })
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent showClose={false} className="vonk-dialog-content--palette">
        <DialogTitle className="sr-only">Zoeken</DialogTitle>
        <DialogDescription className="sr-only">
          Zoek startups, personen, notities, documenten en pagina&apos;s.
        </DialogDescription>
        {/* Filtering happens on the server; vim bindings off so Ctrl+K stays the
            shortcut that closes the palette. */}
        <Command shouldFilter={false} vimBindings={false} label="Zoeken op het platform">
          {/* The input row, with a close button below md: phones have no Esc and
              the palette shows no X, so only the scrim was left, and above the
              keyboard that is a 24px strip. The wrapper takes over the row's
              bottom border so it runs under the button too. */}
          <div className="flex items-center border-b [&>[data-slot=command-input-wrapper]]:flex-1 [&>[data-slot=command-input-wrapper]]:border-b-0">
            {/* 16px below md, like the shared Input: iOS Safari zooms in on a
                smaller field when it gets focus, and the palette opens focused. */}
            <CommandInput
              value={query}
              onValueChange={edit}
              placeholder="Zoek een startup, persoon, notitie, document of pagina…"
              className="text-base md:text-sm"
            />
            <DialogClose className="mr-1 h-11 shrink-0 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:hidden">
              Sluiten
            </DialogClose>
          </div>
          {/* cmdk names the list "Suggestions" unless told otherwise. */}
          <CommandList label="Zoekresultaten" className="h-[min(26rem,55svh)] max-h-none">
            {body}
          </CommandList>
          <p className="hidden border-t px-3 py-2 text-xs text-muted-foreground md:block">
            ↑↓ kiezen · Enter openen · Esc sluiten
          </p>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
